import { motion } from 'framer-motion';
import type { CSSProperties, ReactNode } from 'react';
import type { AccentColor } from './tokens';
import { ACCENT_TOKENS } from './tokens';

/**
 * Badge — small pill shown inline with labels, metric values, or
 * monitor indicators. Matches DESIGN.md §4 "Small labels" typography:
 * uppercase, Inter 500, 11px, 1.5px tracking.
 *
 * Passing `pulse` animates a leading dot via framer-motion — used for
 * the Wall Monitors "live" indicator and similar status markers.
 */
export interface BadgeProps {
  color?: AccentColor;
  icon?: ReactNode;
  pulse?: boolean;
  /** Render as a solid-filled pill instead of tinted + outlined. */
  solid?: boolean;
  /** Use JetBrains Mono rather than Inter uppercase (good for numeric values). */
  mono?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export default function Badge({
  color = 'muted',
  icon,
  pulse = false,
  solid = false,
  mono = false,
  children,
  className,
  style,
}: BadgeProps) {
  const tokens = ACCENT_TOKENS[color];

  const baseStyle: CSSProperties = solid
    ? {
        background: tokens.solid,
        color: '#fff',
        border: '1px solid transparent',
      }
    : {
        background: tokens.bgTint,
        color: tokens.solid,
        border: `1px solid ${tokens.borderTint}`,
      };

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-xs)',
        padding: '3px 8px',
        borderRadius: '999px',
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
        fontWeight: 500,
        fontSize: mono ? '11px' : '10px',
        letterSpacing: mono ? '0' : '1px',
        textTransform: mono ? 'none' : 'uppercase',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        ...baseStyle,
        ...style,
      }}
    >
      {pulse && (
        <motion.span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tokens.solid,
          }}
          animate={{ opacity: [1, 0.35, 1], scale: [1, 1.15, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      {icon && <span style={{ display: 'inline-flex' }}>{icon}</span>}
      {children}
    </span>
  );
}
